import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { subscribeSharedFirestore } from './sharedFirestoreSubscription';

const recordsRef = collection(firestore, 'dscRecords');
const movementsRef = collection(firestore, 'dscMovements');
const companiesRef = collection(firestore, 'dscCompanies');

const displayName = (user) => user?.name || user?.username || user?.email || 'User';

export function subscribeDscRecords(onData, onError) {
  return subscribeSharedFirestore({
    key: 'dscRecords:all',
    onData,
    onError,
    start: (emit, emitError) => onSnapshot(query(recordsRef), (snapshot) => {
      const rows = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      rows.sort((a, b) => String(a.holderName || '').localeCompare(String(b.holderName || '')));
      emit(rows);
    }, emitError),
  });
}

export function subscribeDscMovements(onData, onError) {
  return subscribeSharedFirestore({
    key: 'dscMovements:all',
    onData,
    onError,
    start: (emit, emitError) => onSnapshot(query(movementsRef), (snapshot) => {
      const rows = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      rows.sort((a, b) => (b.performedAt?.seconds || 0) - (a.performedAt?.seconds || 0));
      emit(rows);
    }, emitError),
  });
}

export function subscribeDscCompanies(onData, onError) {
  return subscribeSharedFirestore({
    key: 'dscCompanies:all',
    onData,
    onError,
    start: (emit, emitError) => onSnapshot(query(companiesRef), (snapshot) => {
      const rows = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      rows.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      emit(rows);
    }, emitError),
  });
}

export async function addDscCompany(name, user) {
  const value = String(name || '').trim();
  if (!value) throw new Error('Company / concern name is required.');
  return addDoc(companiesRef, {
    name: value,
    createdById: user?.id || user?.uid || '',
    createdByName: displayName(user),
    createdAt: serverTimestamp(),
  });
}

export async function receiveNewDsc(values, user) {
  const holderName = String(values.holderName || '').trim();
  if (!holderName) throw new Error('DSC holder name is required.');
  const record = {
    holderName,
    companyName: String(values.companyName || '').trim(),
    tokenIdentifier: String(values.tokenIdentifier || '').trim(),
    expiryDate: values.expiryDate || '',
    status: 'In Office',
    currentlyWith: '',
    active: true,
    lastMovementType: 'RECEIVED',
    lastMovementAt: serverTimestamp(),
    lastMovementById: user?.id || user?.uid || '',
    lastMovementByName: displayName(user),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const recordDoc = await addDoc(recordsRef, record);
  await addDoc(movementsRef, {
    dscId: recordDoc.id,
    holderName,
    companyName: record.companyName,
    tokenIdentifier: record.tokenIdentifier,
    movementType: 'RECEIVED',
    fromPerson: '',
    toPerson: 'Office',
    performedById: user?.id || user?.uid || '',
    performedByName: displayName(user),
    performedAt: serverTimestamp(),
  });
  return recordDoc;
}

export async function receiveExistingDsc(dsc, user, allowCorrection = false) {
  if (!dsc?.id) throw new Error('Select a DSC.');
  if (dsc.status === 'In Office' && !allowCorrection) {
    throw new Error('This DSC is already recorded as In Office.');
  }
  const recordDoc = doc(firestore, 'dscRecords', dsc.id);
  await runTransaction(firestore, async (transaction) => {
    const snap = await transaction.get(recordDoc);
    if (!snap.exists()) throw new Error('DSC record no longer exists.');
    const current = snap.data();
    if (current.status === 'In Office' && !allowCorrection) {
      throw new Error('This DSC is already recorded as In Office.');
    }
    transaction.update(recordDoc, {
      status: 'In Office',
      currentlyWith: '',
      lastMovementType: allowCorrection ? 'CORRECTION_RECEIVED' : 'RETURNED',
      lastMovementAt: serverTimestamp(),
      lastMovementById: user?.id || user?.uid || '',
      lastMovementByName: displayName(user),
      updatedAt: serverTimestamp(),
    });
  });
  return addDoc(movementsRef, {
    dscId: dsc.id,
    holderName: dsc.holderName || '',
    companyName: dsc.companyName || '',
    tokenIdentifier: dsc.tokenIdentifier || '',
    movementType: allowCorrection ? 'CORRECTION_RECEIVED' : 'RETURNED',
    fromPerson: dsc.currentlyWith || '',
    toPerson: 'Office',
    performedById: user?.id || user?.uid || '',
    performedByName: displayName(user),
    performedAt: serverTimestamp(),
  });
}

export async function issueDsc(dsc, takenBy, user) {
  if (!dsc?.id) throw new Error('Select a DSC.');
  const custodian = String(takenBy || '').trim();
  if (!custodian) throw new Error('Taken By is required.');
  const recordDoc = doc(firestore, 'dscRecords', dsc.id);
  await runTransaction(firestore, async (transaction) => {
    const snap = await transaction.get(recordDoc);
    if (!snap.exists()) throw new Error('DSC record no longer exists.');
    const current = snap.data();
    if (current.status !== 'In Office') throw new Error('This DSC is not currently available in office.');
    transaction.update(recordDoc, {
      status: 'Outside Office',
      currentlyWith: custodian,
      lastMovementType: 'ISSUED',
      lastMovementAt: serverTimestamp(),
      lastMovementById: user?.id || user?.uid || '',
      lastMovementByName: displayName(user),
      updatedAt: serverTimestamp(),
    });
  });
  return addDoc(movementsRef, {
    dscId: dsc.id,
    holderName: dsc.holderName || '',
    companyName: dsc.companyName || '',
    tokenIdentifier: dsc.tokenIdentifier || '',
    movementType: 'ISSUED',
    fromPerson: 'Office',
    toPerson: custodian,
    performedById: user?.id || user?.uid || '',
    performedByName: displayName(user),
    performedAt: serverTimestamp(),
  });
}

export async function updateDscDetails(id, values) {
  return updateDoc(doc(firestore, 'dscRecords', id), {
    holderName: String(values.holderName || '').trim(),
    companyName: String(values.companyName || '').trim(),
    tokenIdentifier: String(values.tokenIdentifier || '').trim(),
    expiryDate: values.expiryDate || '',
    active: values.active !== false,
    updatedAt: serverTimestamp(),
  });
}


const cleanExpiryDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10);
  return '';
};

export async function bulkImportDscs(rows, user) {
  if (!Array.isArray(rows) || !rows.length) throw new Error('No valid DSC records were found for import.');
  const actorId = user?.id || user?.uid || '';
  const actorName = displayName(user);
  const importedAt = Timestamp.now();
  for (let offset = 0; offset < rows.length; offset += 200) {
    const batch = writeBatch(firestore);
    rows.slice(offset, offset + 200).forEach((values) => {
      const holderName = String(values.holderName || '').trim();
      if (!holderName) return;
      const status = values.status === 'Outside Office' ? 'Outside Office' : 'In Office';
      const currentlyWith = status === 'Outside Office' ? String(values.currentlyWith || '').trim() : '';
      const recordDoc = doc(recordsRef);
      const movementDoc = doc(movementsRef);
      const companyName = String(values.companyName || '').trim();
      const tokenIdentifier = String(values.tokenIdentifier || '').trim();
      batch.set(recordDoc, {
        holderName,
        companyName,
        tokenIdentifier,
        expiryDate: cleanExpiryDate(values.expiryDate),
        status,
        currentlyWith,
        active: true,
        lastMovementType: 'IMPORTED',
        lastMovementAt: importedAt,
        lastMovementById: actorId,
        lastMovementByName: actorName,
        createdAt: importedAt,
        updatedAt: importedAt,
      });
      batch.set(movementDoc, {
        dscId: recordDoc.id,
        holderName,
        companyName,
        tokenIdentifier,
        movementType: 'IMPORTED',
        fromPerson: '',
        toPerson: status === 'Outside Office' ? currentlyWith : 'Office',
        performedById: actorId,
        performedByName: actorName,
        performedAt: importedAt,
      });
    });
    await batch.commit();
  }
}
